import { supabase } from '@rehub/supabase';
import type { ListingData, PropertyCutAsset } from '../types';

export type VoiceoverTone = 'warm' | 'professional' | 'luxury' | 'friendly';
export type VoiceoverLength = 'short' | 'medium' | 'long';

export const VOICEOVER_VOICES = [
  { id: 'JBFqnCBsd6RMkjVDRZzb', label: 'George — warm British male' },
  { id: 'onwK4e9ZLuTAKqWW03F9', label: 'Daniel — authoritative British male' },
  { id: 'pFZP5JQG7iQjIQuC4Bku', label: 'Lily — warm British female' },
  { id: 'Xb7hH8MSUJpSbSDYk0k2', label: 'Alice — confident British female' },
  { id: 'XB0fDUnXU5powFXDhCwa', label: 'Charlotte — refined female' },
] as const;

export function listingVoiceoverContext(listing: ListingData): string {
  return [
    listing.address?.trim(),
    listing.price?.trim(),
    listing.beds != null ? `${listing.beds} bedroom${listing.beds === 1 ? '' : 's'}` : '',
    listing.baths != null ? `${listing.baths} bathroom${listing.baths === 1 ? '' : 's'}` : '',
    listing.tenure ?? '',
    listing.description?.trim(),
  ].filter(Boolean).join('. ');
}

async function invokeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) {
    if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>) && (data as { error?: string }).error) {
      throw new Error((data as { error: string }).error);
    }
    return data as T;
  }
  let message = '';
  const response = (error as { context?: Response }).context;
  if (response && typeof response.json === 'function') {
    const payload = await response.json().catch(() => ({}));
    message = payload?.error || payload?.detail || '';
  }
  throw new Error(message || 'Something went wrong. Try again.');
}

export async function draftVoiceoverScript(args: {
  listing: ListingData;
  features: string[];
  tone: VoiceoverTone;
  length: VoiceoverLength;
  notes: string;
}): Promise<string> {
  const data = await invokeFunction<{ text?: string }>('tours-generate-script', {
    purpose: 'film',
    format: 'script',
    features: args.features,
    tone: args.tone,
    length: args.length,
    custom_notes: args.notes.trim() || undefined,
    property_context: listingVoiceoverContext(args.listing) || undefined,
    language: 'English',
  });
  const text = data.text?.trim();
  if (!text) throw new Error('The draft came back empty. Try again.');
  return text;
}

function readDuration(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    const finish = (value: number | null) => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute('src');
      audio.load();
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), 8000);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      window.clearTimeout(timer);
      finish(Number.isFinite(audio.duration) ? audio.duration : null);
    };
    audio.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    audio.src = url;
  });
}

/** Speak a script with ElevenLabs (1 credit) and save it as a project audio asset. */
export async function speakVoiceover(args: {
  projectId: string;
  text: string;
  voiceId: string;
  voiceLabel: string;
}): Promise<PropertyCutAsset> {
  const spoken = await invokeFunction<{ asset?: PropertyCutAsset }>('generate-tts', {
    text: args.text.trim(),
    provider: 'elevenlabs',
    voice_id: args.voiceId,
    project_id: args.projectId,
    app_type: 'propertycut',
    label: args.voiceLabel,
  });
  if (!spoken.asset?.id || !spoken.asset.signed_url) {
    throw new Error('The voiceover was created, but the file could not be saved.');
  }

  const probed = await readDuration(spoken.asset.signed_url);
  const words = args.text.trim().split(/\s+/).filter(Boolean).length;
  const duration = probed && probed > 0 ? probed : Math.max(8, words / 2.4);
  const { data } = await supabase
    .from('propertycut_assets')
    .update({ duration_seconds: duration })
    .eq('id', spoken.asset.id)
    .select('*')
    .single();
  return (data as PropertyCutAsset | null) ?? { ...spoken.asset, duration_seconds: duration };
}
